import { Box, List, Stack, useMediaQuery, useTheme } from "@mui/material";

// components
import MenuItem from "./MenuItem";

// interfaces
import { Groups as AccountsIcon } from "@mui/icons-material";
import { styled } from "@mui/styles";
import { trackEvent } from "src/Amplitude";
import BrokerUnassigned from "src/icons/BrokerUnassigned";
import RouteItem from "src/model/RouteItem.model";
import Application from "src/pages/Application";
import ReKycApplication from "src/pages/ReKycApplication";
import { PermissionGuard, useFeaturesGuard } from "src/PermissionGuard";
import { useSettingsState } from "src/store/features/settings.slice";
import { useUserSlice } from "src/store/features/user.slice";
import StyledButton from "../dashboard/StyledButton";
import ProfileComponent, { MobilePhoneEmailICons } from "./BrokerHumanisation";
import useVerificationNavigation from "../common/useVerificationNavigation";
import useIsUkUserCountry from "src/utils/hooks/useIsUkUserCountry";

interface MenuProps {
  routes: RouteItem[];
  handleMenuClose: () => void;
}

const Menu = ({ routes, handleMenuClose }: MenuProps) => {
  const { hasFeatureEnabled } = useFeaturesGuard();
  const theme = useTheme();
  const { isUnverified, isPendingVerification } = useUserSlice();
  const { isMultiAccountApplication } = useSettingsState();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { handleVerification } = useVerificationNavigation();
  const isUkUser = useIsUkUserCountry();

  const {
    reKycDetails: { reKyc },
  } = useUserSlice();

  let reKycApplication: RouteItem = {
    title: "Re-KYC",
    menuItem: true,
    path: "/reKycApplication",
    icon: () => <AccountsIcon fontSize="large" style={{ color: "white" }} />,
    component: ReKycApplication,
    permissions: ["user"],
  };

  let additionalAccountApplication: RouteItem = {
    title: "Application",
    menuItem: true,
    path: "/additional-application",
    icon: () => <AccountsIcon fontSize="large" style={{ color: "white" }} />,
    component: Application,
    permissions: ["user"],
  };

  const BrokerContacts = () => (
    <Box px={3}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}>
        {!isUnverified && (
          <Stack
            direction={"row"}
            sx={{
              width: "100%",
              marginBottom: "10px",
              justifyContent: "space-between",
            }}>
            <ProfileComponent setOpen={() => ""} text="light" disableClick={true} />
            <MobilePhoneEmailICons />
          </Stack>
        )}
        {isUnverified && (
          <StyledButton
            onClick={() => {
              trackEvent("Clicked_On_Verify_Account");
              handleVerification();
              handleMenuClose();
            }}>
            Verify Account Now
          </StyledButton>
        )}
      </Stack>
    </Box>
  );

  const PendingVerification = () => (
    <StyledVerificationContainer>
      <StyledApplicationLabel>
        <StyledBrokerImageContainer>
          <BrokerUnassigned width="100%" height="100%" />
        </StyledBrokerImageContainer>
        <StyledApplicationTextContainer>
          <StyledApplicationHeader>Application in review</StyledApplicationHeader>
          <StyledApplicaitonText>
            Your application has been lodged, please check back soon.
          </StyledApplicaitonText>
        </StyledApplicationTextContainer>
      </StyledApplicationLabel>
      <StyledLearnMoreButton
        target="_blank"
        rel="noopener noreferrer"
        href="https://support.calebandbrown.com/en/articles/5826680-navigating-the-portal">
        Learn more about our Portal
      </StyledLearnMoreButton>
    </StyledVerificationContainer>
  );

  return (
    <>
      <List>
        {/* Humanisation, Mobile Broker Profile */}
        {isMobile && isPendingVerification && <PendingVerification />}
        {isMobile && !isPendingVerification && <BrokerContacts />}
        {routes
          .filter((r) => r.menuItem)
          .filter((r) => !(isUkUser && r.path === "/refer-a-friend"))
          .filter((r) => hasFeatureEnabled(r.featurePermission))
          .map((r) => (
            <PermissionGuard key={r.path} permissions={r.permissions}>
              <MenuItem route={r} handleMenuClose={handleMenuClose} />
            </PermissionGuard>
          ))}

        {reKyc && <MenuItem route={reKycApplication} handleMenuClose={handleMenuClose} />}
        {isMultiAccountApplication && (
          <MenuItem route={additionalAccountApplication} handleMenuClose={handleMenuClose} />
        )}
      </List>
    </>
  );
};

export const StyledVerificationContainer = styled("div")(({ theme }) => ({
  display: "flex",
  padding: "8px 16px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "16px",
  alignSelf: "stretch",
}));

export const StyledApplicationLabel = styled("div")(({ theme }) => ({
  display: "flex",
  padding: "16px",
  alignItems: "flex-start",
  gap: "16px",
  alignSelf: "stretch",
  borderRadius: "8px",
  background: "#FEFCF8",
}));

export const StyledBrokerImageContainer = styled("div")(({ theme }) => ({
  display: "flex",
  width: "32px",
  height: "32px",
}));

export const StyledApplicationTextContainer = styled("div")(({ theme }) => ({
  display: "flex",
  width: "100%",
  flexDirection: "column",
  alignItems: "flex-start",
  alignSelf: "stretch",
  gap: "4px",
}));

export const StyledLearnMoreButton = styled("a")(({ theme }) => ({
  textDecoration: "none",
  display: "flex",
  padding: "8px 12px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
  borderRadius: "20px",
  border: "1px solid #FFF",
  backgroundColor: "transparent",
  color: "#FFF",
  fontFamily: "Roobert",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "20px",
  letterSpacing: "-0.32px",

  "&:hover": {
    opacity: 0.3,
  },
}));

export const StyledApplicationHeader = styled("div")(({ theme }) => ({
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "20px",
}));

export const StyledApplicaitonText = styled("div")(({ theme }) => ({
  color: "#002035",
  fontFamily: "Söhne",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "14px",
}));

export default Menu;
